import { ApplicationInsights } from "@microsoft/applicationinsights-web";

// The instrumentation key is baked into the build by the pipeline from the
// static-site's Pulumi stack output (AppInsightsInstrumentationKey), surfaced
// as the GATSBY_-prefixed env var so Gatsby exposes it to client code. When it
// is empty (e.g. local dev) telemetry is disabled rather than sent nowhere.
//
// NOTE: Azure ended technical support for instrumentation-key ingestion on
// 2025-03-31 in favour of connection strings. The SDK is still backward
// compatible with instrumentationKey, and the whole infra->devops->app contract
// is currently key-based (Pulumi output AppInsightsInstrumentationKey). See
// PLA-1788 for the coordinated migration to AppInsightsConnectionString.
let appInsights: ApplicationInsights | undefined;

/**
 * Initialise the Application Insights browser SDK and start streaming telemetry.
 *
 * Idempotent: repeated calls return the already-initialised client. No-ops
 * (returning undefined) when the instrumentation key is empty or when running
 * outside a browser (SSR/build), so a blank key is never baked into a client.
 *
 * @returns the initialised client, or undefined when telemetry is disabled.
 */
export function initAppInsights(): ApplicationInsights | undefined {
  if (appInsights) {
    return appInsights;
  }

  const instrumentationKey =
    process.env.GATSBY_APP_INSIGHTS_INSTRUMENTATION_KEY;

  // No key or no browser (SSR/build) => skip. Guards against baking a blank key.
  if (!instrumentationKey || typeof window === "undefined") {
    return undefined;
  }

  appInsights = new ApplicationInsights({
    config: {
      instrumentationKey,
      // Auto-track SPA route changes as page views (Gatsby client-side nav).
      enableAutoRouteTracking: true,
      disableFetchTracking: false,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
    },
  });

  appInsights.loadAppInsights();
  // Initial load isn't a route change, so send the first page view explicitly.
  appInsights.trackPageView();

  return appInsights;
}

/**
 * Accessor for the initialised Application Insights client.
 *
 * @returns the client once {@link initAppInsights} has run, otherwise undefined
 * (telemetry disabled or not yet initialised).
 */
export function getAppInsights(): ApplicationInsights | undefined {
  return appInsights;
}
